import { useEffect, useMemo, useRef, useState } from 'react';

import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Input } from '@/components/ui/shadcn/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/shadcn/popover';
import { cn, logger } from '@/lib/utils';
import { Check, Loader2, MapPin } from 'lucide-react';

interface AddressSuggestion {
    id: string;
    label: string;
    street?: string;
    house_number?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    country_code?: string;
    lat?: number;
    lng?: number;
}

interface AddressSearchInputProps {
    value: string | null | undefined;
    onChange: (value: string) => void;
    onAddressSelect?: (address: AddressSuggestion) => void;
    placeholder?: string;
    className?: string;
    /** Optional: restrict suggestions to a single country (e.g. 'us'). */
    countryCode?: string;
    minChars?: number;
}

// Minimal typings for the new Autocomplete (New) return objects.
// See: https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#PlacePrediction
type PlacePredictionLike = {
    placeId: string;
    mainText?: { text?: string };
    secondaryText?: { text?: string };
    text?: { text?: string };
    toPlace: () => {
        fetchFields: (options: { fields: string[] }) => Promise<{ place: unknown }>;
    };
};

function getAddressComponent(
    components:
        | Array<{
              types: string[];
              long_name?: string;
              longText?: string;
              short_name?: string;
              shortText?: string;
          }>
        | undefined,
    type: string,
): string {
    const component = components?.find((c) => c.types.includes(type));

    return component?.longText ?? component?.long_name ?? '';
}

export function AddressSearchInput({
    value,
    onChange,
    onAddressSelect,
    placeholder = 'Enter service address',
    className,
    countryCode,
    minChars = 2,
}: AddressSearchInputProps) {
    const normalizedValue = value || '';
    const [open, setOpen] = useState(false);
    const [predictions, setPredictions] = useState<PlacePredictionLike[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [lastStatus, setLastStatus] = useState<string | null>(null);
    const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);
    /** The highlighted suggestion's place id — cmdk's controlled `value`. */
    const [activeValue, setActiveValue] = useState('');
    const isSelectingRef = useRef(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
    const predictionsByIdRef = useRef<Map<string, PlacePredictionLike>>(new Map());

    // More thorough check for Google Maps API readiness (Autocomplete New)
    const checkGoogleReady = (): boolean => {
        if (typeof window === 'undefined') {
            return false;
        }

        try {
            const google = window.google;
            if (!google?.maps?.places) {
                return false;
            }

            // Check that all required classes are available and are functions
            const places = google.maps.places;

            const hasAutocompleteSuggestion =
                typeof (places as unknown as { AutocompleteSuggestion?: { fetchAutocompleteSuggestions?: unknown } }).AutocompleteSuggestion
                    ?.fetchAutocompleteSuggestions === 'function';
            const hasSessionToken = typeof places.AutocompleteSessionToken === 'function';

            if (!hasAutocompleteSuggestion || !hasSessionToken) {
                return false;
            }

            return true;
        } catch (error) {
            console.warn('Error checking Google Maps readiness:', error);
            return false;
        }
    };

    const [googleReady, setGoogleReady] = useState(checkGoogleReady());

    // Poll for Google Maps API to be ready (in case it loads asynchronously)
    useEffect(() => {
        if (googleReady) {
            return;
        }

        // Check immediately
        if (checkGoogleReady()) {
            setGoogleReady(true);
            return;
        }

        // Poll every 100ms for up to 10 seconds (increased from 5 seconds)
        let attempts = 0;
        const maxAttempts = 100;
        const pollInterval = setInterval(() => {
            attempts++;
            if (checkGoogleReady()) {
                setGoogleReady(true);
                clearInterval(pollInterval);
            } else if (attempts >= maxAttempts) {
                clearInterval(pollInterval);
                console.warn(
                    'Google Maps API did not become ready after 10 seconds. Check that GoogleMapsProvider is wrapping this component and the API key is valid.',
                );
            }
        }, 100);

        return () => clearInterval(pollInterval);
    }, [googleReady]);

    useEffect(() => {
        if (!googleReady) {
            return;
        }

        const places = window.google?.maps?.places as unknown as {
            AutocompleteSessionToken?: new () => google.maps.places.AutocompleteSessionToken;
        };

        if (!sessionTokenRef.current && typeof places?.AutocompleteSessionToken === 'function') {
            sessionTokenRef.current = new places.AutocompleteSessionToken();
        }
    }, [googleReady]);

    const shouldSearch = googleReady && normalizedValue.trim().length >= minChars;

    useEffect(() => {
        if (!shouldSearch) {
            predictionsByIdRef.current = new Map();
            setPredictions([]);
            setIsSearching(false);
            return;
        }

        if (!checkGoogleReady()) {
            setIsSearching(false);
            predictionsByIdRef.current = new Map();
            setPredictions([]);
            return;
        }

        const query = normalizedValue.trim();
        const handle = window.setTimeout(() => {
            if (!checkGoogleReady()) {
                setIsSearching(false);
                predictionsByIdRef.current = new Map();
                setPredictions([]);
                return;
            }

            setIsSearching(true);

            const searchTimeout = window.setTimeout(() => {
                setIsSearching(false);
                predictionsByIdRef.current = new Map();
                setPredictions([]);
                setLastStatus('TIMEOUT');
            }, 10000);

            const places = window.google?.maps?.places as unknown as {
                AutocompleteSuggestion?: { fetchAutocompleteSuggestions: (request: Record<string, unknown>) => Promise<{ suggestions?: unknown[] }> };
            };

            try {
                const request: Record<string, unknown> = {
                    input: query,
                    sessionToken: sessionTokenRef.current ?? undefined,
                };

                if (countryCode) {
                    // New API uses includedRegionCodes (CLDR two-character region codes)
                    request.includedRegionCodes = [countryCode];
                }

                places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions?.(request)
                    .then((response) => {
                        window.clearTimeout(searchTimeout);
                        setIsSearching(false);

                        const raw = (response as { suggestions?: unknown[] })?.suggestions ?? [];
                        const placePredictions = raw
                            .map((s) =>
                                s && typeof s === 'object' && 'placePrediction' in s ? (s as { placePrediction?: unknown }).placePrediction : s,
                            )
                            .filter((p): p is PlacePredictionLike => !!p && typeof p === 'object' && 'placeId' in (p as Record<string, unknown>));

                        const map = new Map<string, PlacePredictionLike>();
                        placePredictions.forEach((p) => {
                            if (p.placeId) {
                                map.set(p.placeId, p);
                            }
                        });
                        predictionsByIdRef.current = map;

                        setLastStatus(placePredictions.length > 0 ? null : 'ZERO_RESULTS');
                        setPredictions(placePredictions);
                    })
                    .catch((error) => {
                        console.error('Error fetching autocomplete suggestions:', error);
                        window.clearTimeout(searchTimeout);
                        setIsSearching(false);
                        predictionsByIdRef.current = new Map();
                        setPredictions([]);
                        setLastStatus('ERROR');
                    });
            } catch (error) {
                logger.error('Error fetching autocomplete suggestions:', error);
                window.clearTimeout(searchTimeout);
                setIsSearching(false);
                predictionsByIdRef.current = new Map();
                setPredictions([]);
                setLastStatus('ERROR');
            }
        }, 250);

        return () => {
            window.clearTimeout(handle);
        };
    }, [countryCode, shouldSearch, normalizedValue]);

    useEffect(() => {
        if (!open) {
            setIsSearching(false);
            setPredictions([]);
        } else if (anchorRef.current) {
            // Update popover width when opening
            setPopoverWidth(anchorRef.current.offsetWidth);
        }
    }, [open]);

    // Reset searching state when value changes significantly (user cleared input)
    useEffect(() => {
        if (normalizedValue.trim().length < minChars) {
            setIsSearching(false);
            setPredictions([]);
        }
    }, [normalizedValue, minChars]);

    const items = useMemo(() => {
        return predictions.map((p) => {
            const mainText = p.mainText?.text ?? p.text?.text ?? '';
            const secondaryText = p.secondaryText?.text ?? '';
            const description = p.text?.text ?? [mainText, secondaryText].filter(Boolean).join(', ');

            return {
                placeId: p.placeId,
                description,
                mainText,
                secondaryText,
            };
        });
    }, [predictions]);

    // Start on the first suggestion whenever the list changes, so Enter always has
    // something to act on and the highlight never points at a stale row.
    useEffect(() => {
        setActiveValue(items[0]?.placeId ?? '');
    }, [items]);

    const handleSelect = (placeId: string) => {
        if (!googleReady || !checkGoogleReady()) {
            console.warn('Google Maps not ready');
            return;
        }

        const prediction = predictionsByIdRef.current.get(placeId);
        if (!prediction) {
            console.warn('Missing prediction for selected placeId');
            return;
        }

        isSelectingRef.current = true;
        setIsSearching(true);

        prediction
            .toPlace()
            .fetchFields({
                fields: ['formattedAddress', 'location', 'addressComponents'],
            })
            .then(({ place }) => {
                const typedPlace = place as unknown as {
                    formattedAddress?: string | null;
                    location?: { lat?: () => number; lng?: () => number } | null;
                    addressComponents?: Array<{
                        types: string[];
                        longText?: string;
                        long_name?: string;
                        shortText?: string;
                        short_name?: string;
                    }>;
                };

                const address = typedPlace.formattedAddress ?? '';
                const lat = typedPlace.location?.lat?.();
                const lng = typedPlace.location?.lng?.();

                const components = typedPlace.addressComponents;

                const houseNumber = getAddressComponent(components, 'street_number');
                const street = getAddressComponent(components, 'route');
                const city =
                    getAddressComponent(components, 'locality') ||
                    getAddressComponent(components, 'postal_town') ||
                    getAddressComponent(components, 'administrative_area_level_2');
                const postalCode = getAddressComponent(components, 'postal_code');
                const countryName = getAddressComponent(components, 'country');
                const countryCode =
                    components?.find((component) => component.types.includes('country'))?.shortText ??
                    components?.find((component) => component.types.includes('country'))?.short_name ??
                    '';

                onChange(address);
                onAddressSelect?.({
                    id: placeId,
                    label: address,
                    street: street || undefined,
                    house_number: houseNumber || undefined,
                    city: city || undefined,
                    postal_code: postalCode || undefined,
                    country: countryName || undefined,
                    country_code: countryCode ? countryCode.toUpperCase() : undefined,
                    lat: lat ?? undefined,
                    lng: lng ?? undefined,
                });

                setOpen(false);
                predictionsByIdRef.current = new Map();
                setPredictions([]);

                // Create new session token for next search
                if (window.google?.maps?.places?.AutocompleteSessionToken) {
                    try {
                        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
                    } catch (error) {
                        console.error('Error creating new session token:', error);
                    }
                }
            })
            .catch((error) => {
                console.error('Error getting place details (fetchFields):', error);
            })
            .finally(() => {
                setIsSearching(false);
                isSelectingRef.current = false;
            });
    };

    return (
        <Popover
            open={open}
            onOpenChange={(newOpen) => {
                // Don't allow closing if we're in the process of selecting
                if (!newOpen && isSelectingRef.current) {
                    return;
                }
                setOpen(newOpen);
            }}
        >
            <PopoverAnchor asChild>
                <div ref={anchorRef} className="relative w-full">
                    <MapPin className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2" />
                    <Input
                        ref={inputRef}
                        value={normalizedValue}
                        onChange={(e) => {
                            const next = e.target.value;
                            onChange(next);

                            if (!googleReady) {
                                setOpen(false);
                                return;
                            }

                            if (next.trim().length >= minChars) {
                                setOpen(true);
                            } else {
                                setOpen(false);
                            }
                        }}
                        onKeyDown={(event) => {
                            if (!open || items.length === 0) {
                                return;
                            }

                            // cmdk lowercases the values it stores, so match on that.
                            const currentIndex = Math.max(
                                0,
                                items.findIndex((item) => item.placeId.toLowerCase() === activeValue.toLowerCase()),
                            );

                            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                                event.preventDefault();
                                const delta = event.key === 'ArrowDown' ? 1 : -1;
                                const nextIndex = (currentIndex + delta + items.length) % items.length;
                                setActiveValue(items[nextIndex].placeId);

                                return;
                            }

                            if (event.key === 'Enter') {
                                // Selecting a suggestion, not submitting the form around us.
                                event.preventDefault();
                                handleSelect(items[currentIndex].placeId);

                                return;
                            }

                            if (event.key === 'Escape') {
                                setOpen(false);
                            }
                        }}
                        onFocus={() => {
                            if (googleReady && normalizedValue.trim().length >= minChars) {
                                setOpen(true);
                            }
                        }}
                        onBlur={(e) => {
                            // Don't close if we're in the process of selecting
                            if (isSelectingRef.current) {
                                return;
                            }
                            // Don't close if clicking on popover content
                            const relatedTarget = e.relatedTarget as HTMLElement;
                            if (relatedTarget?.closest('[role="dialog"]') || relatedTarget?.closest('[cmdk-list]')) {
                                return;
                            }
                            // Small delay to allow click events to fire first
                            setTimeout(() => {
                                if (!inputRef.current?.matches(':focus') && !isSelectingRef.current) {
                                    setOpen(false);
                                }
                            }, 200);
                        }}
                        placeholder={placeholder}
                        className={cn('pl-9', className)}
                    />
                </div>
            </PopoverAnchor>

            <PopoverContent
                className="p-0"
                style={popoverWidth ? { width: `${popoverWidth}px` } : undefined}
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <Command shouldFilter={false} value={activeValue} onValueChange={setActiveValue}>
                    <CommandList>
                        {!googleReady && normalizedValue.trim().length >= minChars && (
                            <div className="text-muted-foreground flex items-center justify-center gap-2 p-4 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading Google Maps...
                            </div>
                        )}

                        {googleReady && isSearching && (
                            <div className="text-muted-foreground flex items-center justify-center gap-2 p-4 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Searching…
                            </div>
                        )}

                        {googleReady && !isSearching && items.length === 0 && normalizedValue.trim().length >= minChars && (
                            <CommandEmpty>
                                {lastStatus === 'REQUEST_DENIED'
                                    ? 'Autocomplete request denied. Check that Places API (New) is enabled for your key and referrer restrictions are correct.'
                                    : lastStatus === 'ZERO_RESULTS'
                                      ? 'No addresses found.'
                                      : lastStatus === 'TIMEOUT'
                                        ? 'Autocomplete request timed out.'
                                        : 'No suggestions available.'}
                            </CommandEmpty>
                        )}

                        {!isSearching && items.length > 0 && (
                            <CommandGroup>
                                {items.map((item) => (
                                    <CommandItem
                                        key={item.placeId}
                                        value={item.placeId}
                                        onSelect={() => {
                                            handleSelect(item.placeId);
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <MapPin className="text-muted-foreground mr-2 h-4 w-4 shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-medium">{item.mainText}</p>
                                            {item.secondaryText ? (
                                                <p className="text-muted-foreground truncate text-xs">{item.secondaryText}</p>
                                            ) : null}
                                        </div>
                                        <Check
                                            className={cn('h-4 w-4 shrink-0', normalizedValue === item.description ? 'opacity-100' : 'opacity-0')}
                                        />
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
